'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { useState } from 'react'
import { LuEllipsis } from 'react-icons/lu'

import { SearchInput } from '@/core/components/form/search-input'
import { useAppNow } from '@/features/veluvit/hooks/use-app-now'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import RoomMenuSheet from '@/features/veluvit/components/chats/room-menu-sheet'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import { Chip } from '@/features/veluvit/components/ui/atoms'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { ChipRow } from '@/features/veluvit/components/ui/layout-parts'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { go } from '@/features/veluvit/lib/navigation'
import { openSheet } from '@/features/veluvit/stores/overlay.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Room } from '@/features/veluvit/types/api.types'

type Filter = 'all' | 'unread'

const ROOM_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' })
const ROOM_TIME = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})
const ROOM_DATE = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
})
const ROOM_YEAR = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
})

/** 메신저식 시각 — 오늘은 오전/오후 시:분, 어제는 '어제', 올해는 월.일, 그 전은 연.월.일 */
function stamp(iso: string, now: number) {
  const date = new Date(iso)
  if (ROOM_DAY.format(date) === ROOM_DAY.format(new Date(now)))
    return ROOM_TIME.format(date)
  if (ROOM_DAY.format(date) === ROOM_DAY.format(new Date(now - 86_400_000)))
    return '어제'
  const {
    year = '',
    month = '',
    day = '',
  } = Object.fromEntries(
    ROOM_DATE.formatToParts(date).map((part) => [part.type, part.value])
  )
  return year === ROOM_YEAR.format(new Date(now))
    ? `${month}.${day}`
    : `${year.slice(-2)}.${month}.${day}`
}

/** 미리보기 한 줄 — 행동 표시(*…*)의 별표를 걷고, 내가 보낸 말이면 '나: ' */
function preview(room: Room) {
  if (room.pending) return null
  const last = room.messages.at(-1)
  if (!last) return '대화를 시작해 보세요'
  const text = last.content.replace(/\*/g, '').replace(/\s+/g, ' ').trim()
  return last.role === 'user' ? `나: ${text}` : text
}

/**
 * 대화방 한 줄 — 메신저 문법, 두 줄.
 *  제목 줄: 대화 상대 이름(굵게) · 오른쪽 끝 ⋯ 관리
 *  미리보기 줄: 마지막 말(답장 받는 중이면 '답장 쓰는 중…') 바로 뒤 · 시각, 오른쪽 끝 안 읽음 수
 * 안 읽음이면 이름 · 미리보기가 흰색(굵게)이고 시각이 코랄, 개수 배지가 붙는다. 읽은 방은 이름 · 미리보기를 한 단계씩 회색으로. 보관한 방은 흐리게.
 * 관계 단계 · 시작 장면 머리 줄은 뺐다(2026-10-15 — 옛 시드 작품에만 맞는 기획이라 새 작품과 모양이 갈렸다).
 */
function RoomRow({ room, unread }: { room: Room; unread: number }) {
  const now = useAppNow()
  const { catalog, thumb } = useCatalogMedia()
  const c = catalog.chars[room.workId]
  // 제목은 대화 상대 — 캐릭터 이름(c.short, 주인공). 스튜디오 작품은 작품명이 긴 소설 제목이라 그대로 두면 잘렸다.
  // 서버가 옛 시드 작품 방에 붙이는 기본 이름 '작품명 · 시작 장면'도 캐릭터 이름으로 본다. 이용자가 바꾼 이름 · 분기 방 이름만 그대로 쓴다
  const who = c.short || c.name
  const named = room.title || ''
  const custom =
    !!named &&
    !named.startsWith(`${c.name} · `) &&
    named !== c.name &&
    named !== who
  const title = custom ? named : who
  const text = preview(room)
  const fresh = unread > 0
  return (
    <li
      className={cn(
        'group relative -mx-2 flex min-w-0 items-center rounded-field px-2 transition-colors hover:bg-foreground/[0.04]',
        room.archived && 'opacity-55'
      )}
    >
      <Button
        type="button"
        aria-label={`${title}${fresh ? ` — 안 읽은 답장 ${unread}개` : ''}`}
        onClick={() => {
          useSessionStore.getState().select(room.workId, room.id)
          go(`/chat/${room.id}`)
        }}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3.5 py-3 text-left"
        variant="bare"
      >
        <span className="relative size-13 flex-none">
          <span className="absolute inset-0 overflow-hidden rounded-full bg-surface">
            <SafeImage
              src={thumb(room.workId)}
              alt=""
              fill
              sizes="52px"
              className="object-cover object-[50%_12%]"
              appearance="plain"
            />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          {/* 제목 줄 — 오른쪽 끝은 ⋯ 관리 자리라 비운다 */}
          <span className="flex min-w-0 pr-7">
            {/* 읽은 방은 제목을 살짝 회색으로 — 안 읽은 방(흰 · 굵게)만 눈에 띈다 */}
            <b
              className={cn(
                'min-w-0 truncate text-c16 leading-[1.35]',
                fresh ? 'font-bold text-t1' : 'font-semibold text-t2'
              )}
            >
              {title}
            </b>
          </span>
          {/* 미리보기 줄 — 마지막 말 바로 뒤에 · 시각(말이 길면 말만 줄임표, 시각은 늘 보인다). 안 읽음 수는 줄 오른쪽 끝 */}
          <span className="mt-0.5 flex min-w-0 items-center gap-1 text-c13 leading-[1.5]">
            {text === null ? (
              <span className="min-w-0 truncate font-medium text-point">
                답장 쓰는 중…
              </span>
            ) : (
              <span
                className={cn(
                  'min-w-0 truncate',
                  fresh ? 'font-medium text-t1' : 'text-t4'
                )}
              >
                {text}
              </span>
            )}
            <span
              className={cn(
                'flex-none tabular-nums',
                fresh ? 'font-semibold text-point' : 'text-t4'
              )}
            >
              · {stamp(room.updatedAt, now)}
            </span>
            {fresh && (
              <span
                aria-hidden="true"
                className="ml-auto grid h-4.5 min-w-4.5 flex-none place-items-center rounded-full bg-point px-1.25 text-c10 leading-none font-bold text-inverse tabular-nums"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </span>
        </span>
      </Button>
      {/* ⋯ 관리 — 줄 맨 위 오른쪽(제목 줄 높이). 아래 안 읽음 배지와 오른쪽 끝을 맞춘다. 줄 버튼 안에 넣을 수 없어(버튼 중첩) 위에 띄운다 */}
      <Button
        type="button"
        aria-label="대화 관리"
        onClick={() =>
          openSheet(<RoomMenuSheet room={room} />, { label: '대화 관리' })
        }
        className="absolute top-2.25 right-0.5 grid size-7 cursor-pointer place-items-center rounded-full text-t4 transition-colors hover:bg-foreground/[0.07] hover:text-t1 focus-visible:bg-foreground/[0.07] focus-visible:text-t1"
        variant="bare"
      >
        <LuEllipsis aria-hidden="true" className="size-4.5" />
      </Button>
    </li>
  )
}

/**
 * 내 대화 (레퍼런스 ROUTES.chats + v2 서버 방 목록) — 메신저 목록.
 * 2026-10-15 정리 — 안 읽음(개수 배지 · 굵은 이름 · 코랄 시각) · 전체 / 안 읽음 칩 · 제목은 캐릭터 이름 ·
 * 메신저식 시각 · '나:' 미리보기 · 답장 받는 중 표시 · 보관한 대화는 아래 묶음.
 * 안 읽음 수는 서버가 계정 기준으로 센다(room.unreadCount, backend12.md B12-1) — 서버가 주기 전에는 0 이라 표시가 안 붙는다.
 */
export default function ChatsScreen() {
  const { catalog } = useCatalogMedia()
  const roomList = useSessionStore((state) => state.data?.rooms)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const rooms = (roomList || []).filter((r) => catalog.chars[r.workId])
  const unreadOf = (r: Room) => r.unreadCount || 0
  const q = query.toLowerCase()
  const matches = (r: Room) =>
    !q ||
    [
      r.title,
      catalog.chars[r.workId].name,
      catalog.chars[r.workId].short,
      r.messages.at(-1)?.content,
    ]
      .join(' ')
      .toLowerCase()
      .includes(q)
  const unreadRooms = rooms.filter((r) => unreadOf(r) > 0).length
  const shown = rooms.filter(
    (r) => matches(r) && (filter === 'all' || unreadOf(r) > 0)
  )
  const active = shown.filter((r) => !r.archived)
  const archived = shown.filter((r) => r.archived)

  const list = (items: Room[]) => (
    <ul className="grid list-none gap-x-8 p-0 over10:grid-cols-2 over14:grid-cols-3">
      {items.map((r) => (
        <RoomRow key={r.id} room={r} unread={unreadOf(r)} />
      ))}
    </ul>
  )

  return (
    <>
      <PageHeader title="내 대화" />
      {!rooms.length ? (
        <div className="px-4 pt-3 pb-8 over10:px-8 over10:pt-5">
          <EmptyState
            title="아직 대화가 없어요"
            description="마음에 드는 캐릭터를 만나 첫 대화를 시작해 보세요."
            action={
              <Link
                href="/"
                className="text-point underline underline-offset-4 hover:text-point-soft focus-visible:text-point-soft"
              >
                작품 둘러보기
              </Link>
            }
          />
        </div>
      ) : (
        <div className="pb-8">
          <div className="px-4 pt-3 over10:px-8 over10:pt-5">
            <SearchInput
              id="v2RoomSearch"
              type="search"
              aria-label="대화 검색"
              placeholder="대화 이름 또는 캐릭터 검색"
              delay={120}
              onSearch={setQuery}
              classNames={{
                container: 'min-w-0 over10:max-w-[420px]',
                input: 'max-over6:text-c16',
              }}
            />
          </div>
          {/* 전체 / 안 읽음 — 칩에 개수를 붙여 몇 개 와 있는지 한눈에 */}
          <ChipRow wide className="pt-3">
            <Chip
              on={filter === 'all'}
              aria-pressed={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              전체{' '}
              <span className="tabular-nums opacity-60">{rooms.length}</span>
            </Chip>
            <Chip
              on={filter === 'unread'}
              aria-pressed={filter === 'unread'}
              onClick={() => setFilter('unread')}
            >
              안 읽음
              {unreadRooms > 0 && (
                <span
                  className={cn(
                    'tabular-nums',
                    filter === 'unread' ? 'opacity-60' : 'font-bold text-point'
                  )}
                >
                  {unreadRooms}
                </span>
              )}
            </Chip>
          </ChipRow>

          <div className="px-4 pt-2 over10:px-8">
            {active.length > 0 && list(active)}
            {archived.length > 0 && (
              <section aria-labelledby="chats-archived" className="mt-5">
                <h2
                  id="chats-archived"
                  className="pb-1 text-c12 font-semibold text-t4"
                >
                  보관한 대화{' '}
                  <span className="tabular-nums">{archived.length}</span>
                </h2>
                {list(archived)}
              </section>
            )}
            {!shown.length &&
              (filter === 'unread' && !q ? (
                <EmptyState
                  title="모두 읽었어요"
                  description="새 답장이 오면 여기에 모여요."
                  compact
                />
              ) : (
                <EmptyState
                  title="검색 결과가 없어요"
                  description={`‘${query}’와 맞는 대화를 찾지 못했어요.`}
                  compact
                />
              ))}
          </div>
        </div>
      )}
    </>
  )
}
